import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { useCreateProduct, useUpdateProduct, useMarkets, useFAQs, useAssets } from '@/hooks/usePartnerships';
import { MultiEntityPicker } from './MultiEntityPicker';
import type { Product } from '../../../../shared/types/partnerships';
import { toLines, fromLines } from '../../../../shared/text-lines';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  product?: Product;
}

const CATEGORIES = ['Motors', 'Experience', 'Amplification', 'Enhancers', 'Phygital'] as const;
const STATUSES = ['PUBLISHED', 'DRAFT', 'ARCHIVED'] as const;

interface FormData {
  name: string;
  tagline: string;
  description: string;
  category: string;
  howItWorks: string;
  keyBenefits: string;
  whenToUse: string;
  whyItMatters: string;
  pricing: string;
  deliverables: string;
  mediaRequirements: string;
  creativeRequirements: string;
  bestPractices: string;
  imageUrls: string;
  driveUrl: string;
  notes: string;
  status: string;
}


export function ProductFormDialog({ open, onOpenChange, partnerId, product }: Props) {
  const { t } = useTranslation();
  const isEditing = !!product;
  const create = useCreateProduct();
  const update = useUpdateProduct();
  const { data: markets = [] } = useMarkets();
  const { data: faqs = [] } = useFAQs(partnerId);
  const { data: assets = [] } = useAssets(partnerId);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [selectedFaqIds, setSelectedFaqIds] = useState<string[]>([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>();
  const statusValue = watch('status');
  const categoryValue = watch('category');

  useEffect(() => {
    if (open) {
      setSelectedMarkets(product?.availableMarkets || []);
      setSelectedFaqIds(product?.relatedFaqIds || []);
      setSelectedAssetIds(product?.relatedAssetIds || []);
      reset({
        name: product?.name || '',
        tagline: product?.tagline || '',
        description: product?.description || '',
        category: product?.category || 'Motors',
        howItWorks: toLines(product?.howItWorks),
        keyBenefits: toLines(product?.keyBenefits),
        whenToUse: product?.whenToUse || '',
        whyItMatters: product?.whyItMatters || '',
        pricing: product?.pricing || '',
        deliverables: toLines(product?.deliverables),
        mediaRequirements: product?.mediaRequirements || '',
        creativeRequirements: product?.creativeRequirements || '',
        bestPractices: product?.bestPractices || '',
        imageUrls: toLines(product?.imageUrls),
        driveUrl: product?.driveUrl || '',
        notes: product?.notes || '',
        status: product?.status || 'PUBLISHED',
      });
    }
  }, [open, product, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      name: data.name,
      tagline: data.tagline,
      description: data.description,
      category: data.category as any,
      howItWorks: fromLines(data.howItWorks),
      keyBenefits: fromLines(data.keyBenefits),
      whenToUse: data.whenToUse,
      whyItMatters: data.whyItMatters,
      availableMarkets: selectedMarkets,
      pricing: data.pricing || undefined,
      deliverables: data.deliverables ? fromLines(data.deliverables) : undefined,
      mediaRequirements: data.mediaRequirements || undefined,
      creativeRequirements: data.creativeRequirements || undefined,
      bestPractices: data.bestPractices || undefined,
      relatedFaqIds: selectedFaqIds.length ? selectedFaqIds : undefined,
      relatedAssetIds: selectedAssetIds.length ? selectedAssetIds : undefined,
      imageUrls: data.imageUrls ? fromLines(data.imageUrls) : undefined,
      driveUrl: data.driveUrl || undefined,
      notes: data.notes || undefined,
      status: data.status as any,
    };

    if (isEditing) {
      await update.mutateAsync({ productId: product!.productId, ...payload, expectedUpdatedAt: product!.updatedAt });
    } else {
      await create.mutateAsync({ partnerId, ...payload });
    }
    onOpenChange(false);
  };

  const isBusy = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('partnerships.product_form.title_edit') : t('partnerships.product_form.title_create')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.common.name_required')}</Label>
              <Input {...register('name', { required: true })} placeholder={t('partnerships.product_form.branded_buzz')} />
              {errors.name && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.product_form.category_label')}</Label>
              <Select value={categoryValue} onValueChange={v => setValue('category', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.product_form.tagline_label')}</Label>
            <Input {...register('tagline')} placeholder={t('partnerships.product_form.ugc_masivo_organico_que')} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.description_label')}</Label>
            <Textarea {...register('description')} rows={3} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.product_form.how_it_works_label')}</Label>
              <Textarea {...register('howItWorks')} rows={4} placeholder={t('partnerships.product_form.paso_1_10_paso_2_10')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.product_form.key_benefits_label')}</Label>
              <Textarea {...register('keyBenefits')} rows={4} placeholder={t('partnerships.product_form.beneficio_a_10_beneficio_b')} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.product_form.when_to_use_label')}</Label>
              <Textarea {...register('whenToUse')} rows={2} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.product_form.why_it_matters_label')}</Label>
              <Textarea {...register('whyItMatters')} rows={2} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.available_markets_label')}</Label>
            <div className="flex flex-wrap gap-1.5 p-2 border rounded-md min-h-10">
              {markets.filter(m => m.active).map(m => (
                <button
                  key={m.marketId}
                  type="button"
                  onClick={() => setSelectedMarkets(prev =>
                    prev.includes(m.name) ? prev.filter(n => n !== m.name) : [...prev, m.name]
                  )}
                  className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                    selectedMarkets.includes(m.name)
                      ? 'bg-foreground text-background border-foreground'
                      : 'border-muted-foreground/30 hover:border-foreground/50'
                  }`}
                >
                  {m.name}
                </button>
              ))}
              {markets.filter(m => m.active).length === 0 && (
                <p className="text-xs text-muted-foreground self-center">{t('partnerships.common.no_markets_configured')}</p>
              )}
            </div>
            {selectedMarkets.length > 0 && (
              <p className="text-xs text-muted-foreground">{t('partnerships.common.selected_count', { count: selectedMarkets.length })}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.product_form.pricing_label')}</Label>
              <Input {...register('pricing')} placeholder={t('partnerships.product_form.usd_15_000_min')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.common.drive_link_optional_label')}</Label>
              <Input {...register('driveUrl')} type="url" placeholder="https://drive.google.com/..." />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.product_form.deliverables_label')}</Label>
            <Textarea {...register('deliverables')} rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('partnerships.product_form.media_requirements_label')}</Label>
              <Input {...register('mediaRequirements')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('partnerships.product_form.creative_requirements_label')}</Label>
              <Input {...register('creativeRequirements')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.product_form.best_practices_label')}</Label>
            <Textarea {...register('bestPractices')} rows={2} />
          </div>

          <MultiEntityPicker
            label={t('partnerships.product_form.related_faqs_label')}
            items={faqs.map(f => ({ id: f.faqId, label: f.question, sub: f.category }))}
            selected={selectedFaqIds}
            onChange={setSelectedFaqIds}
            placeholder={t('partnerships.product_form.faq_search_placeholder')}
          />

          <MultiEntityPicker
            label={t('partnerships.product_form.related_assets_label')}
            items={assets.map(a => ({ id: a.assetId, label: a.name, sub: a.assetType }))}
            selected={selectedAssetIds}
            onChange={setSelectedAssetIds}
            placeholder={t('partnerships.product_form.asset_search_placeholder')}
          />

          <div className="space-y-1.5">
            <Label>{t('partnerships.product_form.image_urls_label')}</Label>
            <Textarea {...register('imageUrls')} rows={2} placeholder="https://..." />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.internal_notes_optional_label')}</Label>
            <Textarea {...register('notes')} rows={2} />
          </div>

          <div className="space-y-1.5">
            <Label>{t('partnerships.common.status_label')}</Label>
            <Select value={statusValue} onValueChange={v => setValue('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel')}</Button>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? t('partnerships.common.saving') : isEditing ? t('partnerships.common.save_changes') : t('partnerships.product_form.create_button')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
